import React, { useEffect, useMemo, useState } from "react";
import { fetchWorkouts, fetchRecipes } from "../lib/api";
import { Link } from "react-router-dom";
import { Search, Star, Flame, ChefHat, Timer } from "lucide-react";

const filters = [
    { key: "all", label: "All" },
    { key: "workout", label: "Subscriptions" },
    { key: "recipe", label: "Recipes" },
];

const BrowsePage = () => {
    const [workouts, setWorkouts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [active, setActive] = useState("all");
    const [q, setQ] = useState("");

    useEffect(() => {
        Promise.all([fetchWorkouts(), fetchRecipes()])
            .then(([w, r]) => {
                setWorkouts(w);
                setRecipes(r);
            })
            .catch(() => {});
    }, []);

    const items = useMemo(() => {
        const w = workouts.map((x) => ({ ...x, _kind: "workout" }));
        const r = recipes.map((x) => ({ ...x, _kind: "recipe" }));
        let list = [...w, ...r];
        if (active === "workout") list = w;
        if (active === "recipe") list = r;
        if (q.trim()) {
            const s = q.toLowerCase();
            list = list.filter((p) => {
                const txt = [p.title, p.creator, p.chef, ...(p.tags || [])]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return txt.includes(s);
            });
        }
        return list;
    }, [workouts, recipes, active, q]);

    return (
        <div data-testid="browse-page" className="min-h-[100dvh] pb-32 bg-[#0A0A0A]">
            <header className="px-5 pt-8 pb-4">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-1">Discover</p>
                        <h1 className="font-display text-5xl uppercase leading-none">
                            Hunt your<br />next plan.
                        </h1>
                    </div>
                    <button
                        data-testid="browse-see-demo"
                        onClick={() => (window.location.href = "/demo")}
                        className="shrink-0 mt-2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#E1FF01]/10 border border-[#E1FF01]/40 text-[#E1FF01] font-bold hover:bg-[#E1FF01]/20"
                    >
                        How it works
                    </button>
                </div>
            </header>

            <div className="px-5 mb-4">
                <div className="flex items-center gap-2 bg-[#141414] border border-neutral-800 rounded-full px-4 py-3">
                    <Search size={16} className="text-white/50" />
                    <input
                        data-testid="search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search creators, plans, recipes…"
                        className="bg-transparent outline-none text-sm flex-1 placeholder:text-white/40"
                    />
                </div>
            </div>

            <div className="px-5 flex gap-2 mb-5 overflow-x-auto hide-scrollbar">
                {filters.map((f) => (
                    <button
                        key={f.key}
                        data-testid={`filter-${f.key}`}
                        onClick={() => setActive(f.key)}
                        className={`shrink-0 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition-all ${
                            active === f.key
                                ? "bg-[#E1FF01] text-[#0A0A0A] border-[#E1FF01]"
                                : "bg-transparent text-white border-neutral-800 hover:border-white/40"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="px-3 grid grid-cols-2 gap-2.5">
                {items.map((p, i) => {
                    const tall = i % 5 === 0 || i % 5 === 3;
                    const isWorkout = p._kind === "workout";
                    const monthly = isWorkout
                        ? p.tiers?.find((t) => t.id === "monthly") || p.tiers?.[0]
                        : null;
                    return (
                        <Link
                            key={`${p._kind}-${p.id}`}
                            to={isWorkout ? `/workout/${p.id}` : `/recipe/${p.id}`}
                            data-testid={`browse-card-${p.id}`}
                            className={`relative group rounded-2xl overflow-hidden border border-neutral-800 hover:border-[#E1FF01]/60 transition-all ${
                                tall ? "row-span-2 aspect-[3/5]" : "aspect-[4/5]"
                            }`}
                        >
                            <img
                                src={p.image_url}
                                alt={p.title}
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                            <div className="absolute top-2 left-2">
                                <span
                                    className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                        isWorkout
                                            ? "bg-[#E1FF01] text-[#0A0A0A]"
                                            : "bg-white text-[#0A0A0A]"
                                    }`}
                                >
                                    {isWorkout ? (
                                        <span className="inline-flex items-center gap-1">
                                            <Flame size={9} /> Sub
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1">
                                            <ChefHat size={9} /> Recipe
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-3">
                                <p className="text-[10px] text-white/60 uppercase tracking-wider mb-0.5">
                                    @{(p.creator || p.chef)?.toLowerCase().replace(/\s+/g, "")}
                                </p>
                                <h3 className="font-display text-lg uppercase leading-tight line-clamp-2">
                                    {p.title}
                                </h3>
                                <div className="flex items-center justify-between mt-1.5">
                                    {isWorkout ? (
                                        <span className="text-[11px] text-white/70 flex items-center gap-1">
                                            <Star size={10} className="fill-[#E1FF01] text-[#E1FF01]" />
                                            {p.rating}
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-white/70 flex items-center gap-1">
                                            <Timer size={10} /> {p.prep_min}m
                                        </span>
                                    )}
                                    <span className="font-display text-base text-[#E1FF01]">
                                        {isWorkout ? `$${monthly.price}/mo` : `${p.calories} kcal`}
                                    </span>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {items.length === 0 && (
                <div data-testid="browse-empty" className="text-center text-white/50 py-16 px-6">
                    <p className="font-display text-2xl uppercase">No results</p>
                    <p className="text-sm mt-1">Try a different filter or search term.</p>
                </div>
            )}
        </div>
    );
};

export default BrowsePage;
